import { VisitorHeaderDataProviderProps } from '@care/navigation';
import { MegaMenuNavSection } from '@care/navigation/dist/components/GlobalNavIA/types';
import { LinkKind } from '@care/navigation/dist/components/GlobalNavIA/NavLinkList/NavLinkList';
import {
  seoNavigationLinkSection_seoNavigationLinkSection_SEONavigationLinkSectionSuccess_seoNavigationLinkSection as SEONavigationLinkSection,
  seoNavigationLinkSection_seoNavigationLinkSection_SEONavigationLinkSectionSuccess_seoNavigationLinkSection_childCareLinks as SEONavigationLink,
} from '@/__generated__/seoNavigationLinkSection';
import { getMoreInfoCommonLinks } from './getMoreInfoCommonLinks';
import { buildVgnLinkList } from './buildVgnLinkList';
import { buildFindCareVerticalSection, keyToCareCategoryMap } from './buildFindCareVerticalSection';

const keyToSectionTitleMap: Record<string, string> = Object.freeze({
  childCareLinks: 'Child care',
  seniorCareLinks: 'Senior care',
  adultCareLinks: 'Adult care',
  petCareLinks: 'Pet care',
  housekeepingLinks: 'Housekeeping',
  tutoringLinks: 'Tutoring',
});

const sortVerticalOrder: Record<string, number> = Object.freeze({
  childCareLinks: 1,
  seniorCareLinks: 2,
  adultCareLinks: 3,
  petCareLinks: 4,
  housekeepingLinks: 5,
  tutoringLinks: 6,
});

/**
 * Generates the content for the "Find Care" menu section in the navigation.
 * It builds a structured array of vertical sections based on the provided navigation link section.
 * Each vertical section contains links related to a specific care category.
 * If the viewport is not large, common links will be added below the vertical sections.
 * @param baseURL - The base URL for the application.
 * @param navigationLinkSection - The section containing SEO navigation links.
 * @param isLargeViewport - A boolean indicating if the viewport is large. Defaults to false.
 * @returns An object containing the structured find care menu links.
 */

export const getFindCareMenuContent = (
  baseURL: string,
  navigationLinkSection: SEONavigationLinkSection,
  isLargeViewport: boolean = false
): VisitorHeaderDataProviderProps['findCareMenuLinks'] => {
  const moreInfoCommonLinks = getMoreInfoCommonLinks(baseURL);
  const findCareContent: MegaMenuNavSection['items'] = [];

  Object.entries(navigationLinkSection)
    // filter out __typename and jobsLinks keys
    .filter(([key]: [string, SEONavigationLink[]]) => key !== '__typename' && key !== 'jobsLinks')
    // sort by the predefined vertical order
    .sort(([a], [b]) => {
      return (sortVerticalOrder[a] ?? 0) - (sortVerticalOrder[b] ?? 0);
    })
    .forEach(([key, linkList]: [string, SEONavigationLink[]]) => {
      const vgnLinkList: LinkKind[] = buildVgnLinkList(baseURL, key, linkList, isLargeViewport);

      const verticalSection = buildFindCareVerticalSection(
        baseURL,
        key,
        vgnLinkList,
        isLargeViewport,
        moreInfoCommonLinks
      );

      // Add the vertical section to findCareContent array.
      findCareContent.push({
        id: keyToCareCategoryMap[key],
        title: keyToSectionTitleMap[key],
        items: verticalSection,
      });
    });

  // Add the "More Info" common links to the findCareContent if the viewport is not large.
  if (!isLargeViewport) {
    findCareContent.push({
      id: 'more-info',
      kind: 'group',
      items: moreInfoCommonLinks,
    });
  }

  return {
    id: 'find-care',
    title: 'Find care',
    items: findCareContent,
  };
};
